import { useEffect, useRef } from 'react';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import { useQueryClient } from '@tanstack/react-query';
import { useNotificationStore } from '../store/useNotificationStore';
import { usePresenceStore } from '../store/usePresenceStore';
import { useAuthStore } from '../store/useAuthStore';


const NOTIFICATION_SOUND = '/assets/sounds/alert.mp3';

export const useSocketManager = () => {
    const queryClient = useQueryClient();
    const showNotify = useNotificationStore(state => state.show);
    const setOnlineUsers = usePresenceStore(state => state.setOnlineUsers);
    const accessToken = useAuthStore(state => state.accessToken);
    const clientRef = useRef(null);

    const addNotification = useNotificationStore(state => state.addNotification);

    const playPing = () => {
        const audio = new Audio(NOTIFICATION_SOUND);
        audio.play().catch(e => console.log("Audio play blocked by browser"));
    };

    useEffect(() => {
        if (!accessToken) return;

        const socket = new SockJS(`${process.env.REACT_APP_API_URL}/ws-travel`);
        const client = new Client({
            webSocketFactory: () => socket,
            connectHeaders: { Authorization: `Bearer ${accessToken}` },
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            onConnect: () => {
                console.log('⚡️ Connecté au centre de synchronisation');

                const refreshDashboard = () => {
                    queryClient.invalidateQueries(['agent-dashboard']);
                };

                // 1. Canal PRÉSENCE (Mise à jour globale)
                client.subscribe('/topic/presence', (msg) => {
                    setOnlineUsers(JSON.parse(msg.body));
                });

                // 2. Canal DOSSIERS (Auto-refresh de la file d'attente)
                client.subscribe('/topic/dossiers-pending', (msg) => {
                    const data = JSON.parse(msg.body);
                    // Invalide le cache : déclenche un GET /api/orders/pending-validation auto
                    queryClient.invalidateQueries(['pending-validation']);
                    playPing();
                    addNotification({
                        type: 'dossier',
                        title: 'Nouveau Dossier',
                        message: `Dossier #${data.referenceCode} en attente de validation.`,
                        reference: data.referenceCode
                    });
                });

                // 3. Canal FACTURES (Notification proactive)
                client.subscribe('/topic/factures', (msg) => {
                    const data = JSON.parse(msg.body);
                    queryClient.invalidateQueries(['factures']);

                    playPing();
                    addNotification({
                        type: 'facture',
                        title: 'Facture Générée',
                        message: `La facture ${data.numeroFacture} est prête.`,
                        reference: data.numeroFacture
                    });
                });
            },
            onStompError: (frame) => console.error('STOMP Error:', frame.headers['message'])
        });

        client.activate();
        clientRef.current = client;

        return () => client.deactivate();
    }, [accessToken, queryClient, showNotify, setOnlineUsers]);
};